import { Currency } from "../../types/currency";
import {
	EventCostCategoryResource,
	EventFormValues,
	EventSaveRequest,
	EventUpdateResource,
	EventUserSaveRequest,
	ITenantOptionResource,
} from "../../types/event";

export function mapEventFormValuesToRequest(values: EventFormValues): EventSaveRequest {
	if (values.eventType === null || values.start === null || values.end === null || values.primaryLanguage === null) {
		throw new Error("Event type is required");
	}
	if (
		values.tenantPlannedPersonCounts.length > 0 &&
		values.tenantPlannedPersonCounts.some((pc) => pc.tenant === null)
	) {
		throw new Error("Clean up state of tenantPlannedPersonCounts");
	}

	const reducedAddress = {
		city: values.address.city,
		zip: values.address.zip,
		street: values.address.street,
		houseNumber: values.address.houseNumber,
		countryId: values.address.country?.id || "",
		additionalInfo: values.address.additionalInfo,
	};

	const isAddressSet = values.address && Object.values(reducedAddress).some((v) => !!v);

	const address: EventSaveRequest["address"] | null =
		values.eventType.addressRequirement !== "prohibited"
			? isAddressSet
				? {
						city: values.address.city,
						zip: values.address.zip,
						street: values.address.street,
						houseNumber: values.address.houseNumber,
						countryId: values.address.country?.id || "",
						additionalInfo: values.address.additionalInfo,
					}
				: null
			: null;

	return {
		translations:
			values.eventType.additionalInfoRequirement === "prohibited"
				? values.translations.map((translation) => ({
						locale: translation.locale,
						name: translation.name,
						additionalInfo: null,
					}))
				: values.translations,
		// Step 1
		eventTypeId: values.eventType.id,
		start: values.start,
		end: values.end,
		eventGoal: values.eventType.eventGoalRequirement !== "prohibited" ? values.eventGoal : null,
		timezone: values.timezone,
		primaryLanguageIsoCode: values.primaryLanguage.isoCode,
		languageIsoCodes:
			values.eventType.eventLanguageRequirement !== "prohibited" ? values.languages.map((l) => l.isoCode) : [],
		locationId:
			values.eventType.locationRequirement === "prohibited"
				? null
				: values.location?.id === undefined
					? null
					: values.location.id,
		organizerId:
			values.eventType.organizerRequirement !== "prohibited" && values.organizer?.id ? values.organizer?.id : null,
		visibility: values.visibility,
		imageId: values.image?.id || null,
		// Step 1.5
		address: address,
		//Step 2
		eventUsers: values.eventType.userResponsibilityRequirements
			.filter(({ requirement }) => requirement !== "prohibited")
			.filter(({ userResponsibility }) => !!values.eventUsers?.[userResponsibility.id]?.length)
			.map(
				({ userResponsibility }): EventUserSaveRequest => ({
					userResponsibilityId: userResponsibility.id,
					userIds: values.eventUsers[userResponsibility.id].map(({ id }) => id),
				}),
			),
		// Step 3
		isInviteOnly:
			values.eventType.participationPermissionRequirement !== "prohibited" && values.isInviteOnly !== null
				? values.isInviteOnly === "true"
				: null,
		link: values.eventType.linkToEventRequirement !== "prohibited" ? values.link : null,
		tenantPlannedPersonCounts:
			values.eventType.plannedPersonCountRequirement !== "prohibited"
				? values.tenantPlannedPersonCounts.map((pc) => ({
						tenantId: (pc.tenant as ITenantOptionResource).id,
						plannedPersonCount: pc.plannedPersonCount,
					}))
				: [],
		// Step 4
		currencyId: values.currency?.id ?? null,
		eventCostCategories: values.eventCostCategories.map((c) => ({
			costCategoryId: c.costCategory.id,
			plannedCost: c.plannedCost,
			actualCost: c.actualCost,
			comment: c.commentAllowed ? c.comment : null,
		})),
		// Step 5
		format: values.eventType.formatRequirement !== "prohibited" ? values.format : null,
		eventCategoryId:
			values.eventType.eventCategoryRequirement !== "prohibited" && values.eventCategory?.id
				? values.eventCategory?.id
				: null,
		industryIds: values.eventType.industryRequirement !== "prohibited" ? values.industries.map((i) => i.id) : [],
		targetGroupId:
			values.eventType.targetGroupRequirement !== "prohibited" && values.targetGroup?.id
				? values.targetGroup?.id
				: null,
		regionId: values.eventType.regionRequirement !== "prohibited" && values.region?.id ? values.region?.id : null,
		primaryOrganizationalUnitId:
			values.eventType.primaryOrganizationalUnitRequirement !== "prohibited" && values.primaryOrganizationalUnit?.id
				? values.primaryOrganizationalUnit.id
				: null,
		organizationalUnitIds:
			values.eventType.organizationalUnitRequirement !== "prohibited"
				? values.organizationalUnits.map((o) => o.id)
				: [],
		tagIds: values.eventType.tagRequirement !== "prohibited" ? values.tags.map((t) => t.id) : [],
		preferenceIds: values.eventType.preferenceRequirement !== "prohibited" ? values.preferences.map((p) => p.id) : [],
		isHighlight: values.eventType.isHighlightRequirement !== "prohibited" ? values.isHighlight : false,
	};
}

export function mapEventToFormValues(values: EventUpdateResource, defaultCurrency: Currency): EventFormValues {
	const eventType = values.eventType;

	const eventCostCategories: EventCostCategoryResource[] = eventType
		? eventType.costCategoryRequirements
				.filter((req) => req.requirement !== "prohibited")
				.map(
					(req) =>
						values.eventCostCategories.find((c) => c.costCategory.id === req.costCategory.id) ?? {
							costCategory: req.costCategory,
							plannedCost: null,
							actualCost: null,
							commentAllowed: false,
							comment: null,
						},
				)
		: values.eventCostCategories;

	return {
		...values,
		currency: values.currency ?? defaultCurrency,
		isInviteOnly: values.isInviteOnly === null ? null : values.isInviteOnly ? "true" : "false",
		address: values.address || {
			street: null,
			houseNumber: null,
			zip: null,
			city: null,
			country: null,
			additionalInfo: null,
		},
		eventCostCategories,
	};
}

export function prepareEventForDuplication(values: EventUpdateResource): EventUpdateResource {
	const costCategories: EventCostCategoryResource[] | undefined = values.eventType?.costCategoryRequirements
		.filter((costCategoryRequirement) => costCategoryRequirement.requirement !== "prohibited")
		.map((costCategoryRequirement) => ({
			costCategory: costCategoryRequirement.costCategory,
			plannedCost: null,
			actualCost: null,
			comment: null,
			commentAllowed: false,
		}));

	return {
		...values,
		start: null,
		end: null,
		eventUsers: {},
		eventCostCategories: costCategories ?? [],
		link: "",
	};
}
